<template>
  <div class="area-filter-selector" :class="{ 'move-only': !applyFilterToMap }">
    <select v-bind:value="selectedPref" @change="onPrefSelected" class="uk-select" aria-label="Select Area" :disabled="!initialized">
      <option :value="''" key="0">{{ prefConfig.label }}</option>
      <option v-for="pref in prefs" :value="pref" :key="pref">{{ pref }}</option>
    </select>
    <select v-bind:value="selectedCity" @change="onCitySelected" class="uk-select" aria-label="Select City" :disabled="!selectedPref || !cities?.length">
      <option :value="''" key="0">{{ cityConfig.label }}</option>
      <option v-for="city in cities" :value="city" :key="city">{{ city }}</option>
    </select>
    <button v-if="!applyFilterToMap" @click="onClickedMove" class="uk-button move-to-area" :disabled="!isAreaSelected" v-wave><span>{{ messages.map_pan_to_selected_area }}</span></button>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref, watch } from 'vue'
import uikit from 'uikit'
import store from '@/store'
import MapService from '@/services/MapService.ts'
import PointService from '@/services/PointService.ts'

const messages = storelocator.messages

/**
 * 都道府県と市区郡の区切り文字
 * api_aggregated_pointsの仕様に合わせる
 */
const AREA_SEPARATOR = '_'

/**
 * デフォルトのポイントが1つの場合のズームレベル
 * MapService.map.getZoom()で現在のズームレベルは確認できる
 */
const DEFAULT_POINT_ZOOM = 16

/**
 * デフォルトのポイントが複数の場合の最大ズームレベル
 * MapService.map.getZoom()で現在のズームレベルは確認できる
 */
const DEFAULT_POINTS_MAX_ZOOM = 14

interface AreaConfig {
  /**
   * 選択された追加フィールドを識別するためのキー
   */
  key: string
  /**
   * セレクトボックスが選択されていない場合に表示するラベル
   */
  label: string
  /**
   * 絞り込みを判定するポイントの追加フィールドのキー
   */
  point_extra_field_key: string
}

const props = defineProps({
  pointZoom: {
    type: Number,
    default: DEFAULT_POINT_ZOOM,
    required: false
  },
  pointsMaxZoom: {
    type: Number,
    default: DEFAULT_POINTS_MAX_ZOOM,
    required: false
  },
  prefConfig: {
    type: Object as PropType<AreaConfig>,
    required: true
  },
  /**
   * 位置とズームを固定する都道府県
   */
  fixedLocationPrefs: {
    type: Object as PropType<{
      /**
       * 都道府県コード
       */
      [key: string]: {
        /**
         * 緯度
         */
        latitude: number,
        /**
         * 経度
         */
        longitude: number,
        /**
         * ズームレベル
         */
        zoom: number,
      }
    }>,
    required: false,
    default: () => ({})
  },
  cityConfig: {
    type: Object as PropType<AreaConfig>,
    required: true
  },
  applyFilterToMap: {
    type: Boolean,
    required: false,
    default: false
  }
})

const emit = defineEmits(['onMoved'])

/**
 * true: 都道府県か市区郡が選択されている
 */
const isAreaSelected = computed(() => selectedCity.value || selectedPref.value)

/**
 * 選択中の都道府県の名前
 */
const selectedPref = props.applyFilterToMap ? computed(() => store.state.filters.selectedExtraFields?.pref?.value || '') : ref('')

/**
 * 選択中の市区郡の名前
 */
const selectedCity = props.applyFilterToMap ? computed(() => store.state.filters.selectedExtraFields?.city?.value || '') : ref('')

/**
 * 集約した都道府県
 * @returns {Object} { pref1: { bounds: [], center: { latitude: 0, longitude: 0 }, order: 0 }, ... }
 */
const groupedPrefs = computed(() => PointService.aggregates.prefs.groups)

/**
 * 集約した市区郡
 * @returns {Object} { pref_city: { bounds: [], center: { latitude: 0, longitude: 0 }, order: 0 }, ... }
 */
const groupedCities = computed(() => PointService.aggregates.cities.groups)

/**
 * 都道府県の選択肢 [pref1, pref2, ...]
 */
const prefs = Object.values(storelocator.area).flatMap(a => Object.values(a.areas).map(area => area.area_name))

/**
 * 都道府県名とエリアコードのリスト
 * @type {Array} [{ areaName: 都道府県名, areaCode: エリアコード }, ...]
 */
const areas: Array<{ areaName: string, areaCode: string}> = Object.values(storelocator.area).flatMap(a => Object.values(a.areas).map(area => ({ areaName: area.area_name, areaCode: area.area_code })))

/**
 * 市区郡の選択肢
 * 都道府県の選択状態に応じて出し分ける
 * @returns {Array} [city1, city2, ...]
 */
const cities = computed(() => citiesGroupedByPref.value[selectedPref.value] ?? [])

/**
 * 都道府県ごとの市区郡が初期化されているか
 */
const initialized = ref(false)

/**
 * 都道府県ごとの市区郡 { pref: [city1, city2, ...], ... }
 */
const citiesGroupedByPref = ref<Record<string, string[]>>({})

/**
 * 都道府県選択
 */
const onPrefSelected = (event: Event) => {
  const pref = event.target.value

  if (props.applyFilterToMap) {
    if (selectedCity.value) {
      applyCityToFilter('')
    }

    applyPrefToFilter(pref)
    applyPrefToMap(pref)
  } else {
    if (selectedCity.value) {
      selectedCity.value = ''
    }

    selectedPref.value = pref
  }
}

/**
 * 市区郡選択
 */
const onCitySelected = (event: Event) => {
  const city = event.target.value

  if (props.applyFilterToMap) {
    applyCityToFilter(city)
    applyCityToMap(city)
  } else {
    selectedCity.value = city
  }
}

/**
 * 移動ボタンクリック
 */
const onClickedMove = () => {
  if (selectedCity.value) {
    applyCityToMap(selectedCity.value)
  } else {
    applyPrefToMap(selectedPref.value)
  }

  emit('onMoved', selectedPref.value, selectedCity.value)

  // モーダルが非表示になるまで待機
  setTimeout(() => {
    selectedPref.value = ''
    selectedCity.value = ''
  }, 300)
}

/**
 * 都道府県を絞り込みに適用
 * @param {string} pref 都道府県名
 */
const applyPrefToFilter = (pref: string) => {
  store.dispatch("changeSelectedExtraField", { key: props.prefConfig.key, extraField: { key: props.prefConfig.point_extra_field_key, value: pref } })
  PointService.applyFilter()
}

/**
 * 市区郡を絞り込みに適用
 * @param {string} city 市区郡名
 */
const applyCityToFilter = (city: string) => {
  store.dispatch("changeSelectedExtraField", { key: props.cityConfig.key, extraField: { key: props.cityConfig.point_extra_field_key, value: city } })
  PointService.applyFilter()
}

/**
 * 都道府県を地図に適用
 * @param {string} pref 都道府県名
 */
const applyPrefToMap = (pref: string) => {
  if (!pref) {
    return
  }

  const areaCode = areas.find(a => a.areaName === pref)?.areaCode
  if (areaCode && props.fixedLocationPrefs[areaCode]) {
    // 位置とズームレベルが指定されている場合
    const { latitude, longitude, zoom } = props.fixedLocationPrefs[areaCode]
    MapService.map.setCenter(new google.maps.LatLng(latitude, longitude))
    MapService.map.setZoom(zoom)
  } else {
    fitBoundsFromGroupedArea(groupedPrefs.value[pref])
  }
}

/**
 * 市区郡を地図に適用
 * @param {string} city 市区郡名
 */
const applyCityToMap = (city: string) => {
  if (city) {
    fitBoundsFromGroupedArea(groupedCities.value[selectedPref.value + AREA_SEPARATOR + city])
  }
}

/**
 * 集約した地域をもとにポイントの表示範囲を更新
 * @param {Object} groupedArea { bounds: [], center: { latitude: 0, longitude: 0 } }
 */
const fitBoundsFromGroupedArea = (groupedArea) => {
  if (groupedArea.bounds.length === 1) {
    // ポイントが1つの場合はそのポイントにズーム
    MapService.map.panTo(new google.maps.LatLng(groupedArea.center.latitude, groupedArea.center.longitude))
    MapService.map.setZoom(props.pointZoom)
  } else {
    // ポイントが複数の場合は全てのポイントを表示する
    const bounds = new google.maps.LatLngBounds()
    groupedArea.bounds.forEach(bound => bounds.extend(new google.maps.LatLng(bound.latitude, bound.longitude)))
    MapService.fitBounds(bounds, props.pointsMaxZoom)
  }
}

/**
 * true: 地図が読み込み完了
 */
const isMapReady = computed(() => store.state.mapReady === 0)

watch(() => isMapReady.value, (isMapReady) => {
  if (!initialized.value && isMapReady) {
    init()
  }
})

const init = async () => {
  // 集約した地域を読み込むために待機
  await new Promise((resolve) => setTimeout(resolve, 100))

  // 読み込まれていない場合はさらに待機
  let attempts = 0;
  while (!PointService.aggregates && attempts < 3) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    attempts++;
  }

  if (PointService.aggregates) {
    initialized.value = true
    citiesGroupedByPref.value = generateCitiesGroupedByPref(groupedCities.value)

    // クエリパラメータでエリアコードが指定されている場合は表示範囲を更新
    const areaCodeParam = storelocator.url_params.area
    if (areaCodeParam) {
      const areaName = areas.find(a => a.areaCode === areaCodeParam)?.areaName
      if (areaName) {
        applyPrefToMap(areaName)
        if (props.applyFilterToMap) {
          applyPrefToFilter(areaName)
        }
      }
    }
  } else {
    uikit.notification({
      message: messages.map_failed_to_load_areas,
      pos: 'top-center',
      timeout: 3000
    })
  }
}

/**
 * 集約した市区郡から都道府県ごとの市区郡リストを生成
 * @param {Array} groups
 * @returns {Object} { pref: [city1, city2, ...], ... }
 */
const generateCitiesGroupedByPref = (groups): Record<string, string[]> => {
  const list: Record<string, string[]> = {}
  const cities = []
  for (const [key, val] of Object.entries(groups)) {
    cities.push({ key, order: val.order })
  }

  cities.sort((a, b) => a.order - b.order)

  cities.forEach((c) => {
    const [pref, city] = c.key.split(AREA_SEPARATOR)
    if (city !== '') {
      if (list[pref]) {
        list[pref].push(city)
      } else {
        list[pref] = [city]
      }
    } 
  })

  return list
}
</script>

<style lang="scss" scoped>
.area-filter-selector {
  width: 100%;
  display: flex;
  justify-content: space-between;
  column-gap: 4%;
  select {
    border: 1px solid #eee;
    border-radius: 2px;
    font-size: 0.875rem !important;
  }
  &.move-only {
    flex-direction: column;
    align-items: center;
    row-gap: 1rem;
    select {
      text-align: center;
      &:last-of-type {
        margin-bottom: 1rem;
      }
    }
  }
  .move-to-area {
    height: 40px;
    padding: 0px 20px;
    transition: all 0.3s;
    border-radius: 20px;
    color: #fff;
    background-color: var(--theme-color-light98);
    &:not(:disabled) {
      background-color: var(--theme-color);
      span {
        opacity: 0.8;
      }
      &:hover {
        span {
          opacity: 1;
        }
      }
    }
  }
}
</style>
